/**
 * Noibu Feature Flag — product page experiment ("pdp-above-the-fold").
 *
 * Structurally identical to assets/noibu-flag-cart.js,
 * assets/noibu-flag-collection-landing.js and assets/noibu-feature-flag.js.
 * Kept as its own file for the same reason: each defines the control for a
 * different experiment, and folding a new flag into a running one changes its
 * control mid-flight.
 *
 * This experiment is unusual in that variation B changes no markup at all —
 * it is a stylesheet scoped to .nff-pdp--b. This script only sets that class.
 * If it never runs, every rule in the stylesheet fails to match and the page
 * is exactly today's page.
 *
 *   current-experience  -> html.nff-pdp--a  (control, the default)
 *   compact-buy-box     -> html.nff-pdp--b
 *
 * PREREQUISITE — READ THIS BEFORE ASSUMING THE TEST IS RUNNING
 * NOIBUJS_CONFIG.feature_flag_key must be provisioned for this store. Without
 * it, collect-core never injects collect-ff.js, window.NoibuFeatureFlag is
 * never defined, and nobody is bucketed. The home-page experiment recorded
 * zero exposures between 2026-08-21 and 2026-08-26 for exactly this reason,
 * while the page looked perfectly healthy. reportState() makes that state
 * visible in Explorations instead of silent.
 */
(function () {
  'use strict';

  var FLAG = '2083-pdp-above-the-fold';
  var CONTROL = 'current-experience';
  var VARIATION_B = 'compact-buy-box';
  var CACHE_KEY = 'nff.pdp-above-the-fold';

  var REPORT_KEY = 'nff_pdp-above-the-fold';
  var STATE_ATTR = 'data-nff-pdp-state';

  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-pdp--a';
  CLASSES[VARIATION_B] = 'nff-pdp--b';

  var root = document.documentElement;

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // addCustomAttribute comes from collect-core, which loads independently of
    // collect-ff — so reporting works in precisely the case where flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // Not terminal — collect-core may still be booting.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load — is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) return;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-pdp', variation);

    document.dispatchEvent(
      new CustomEvent('noibu:pdp-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Cached so a repeat collector — this store's primary customer, who
    // researches a drop across several sessions before buying — does not see
    // the buy box move between visits. Wrapped because storage throws in
    // private mode and in embedded webviews.
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_pdp=compact-buy-box.
    try {
      var value = new URLSearchParams(window.location.search).get('nff_pdp');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable — fall through to the flag. */
    }
    return null;
  }

  function render(client) {
    var variation = client.getStringValue(FLAG, CONTROL);

    if (variation === VARIATION_B) {
      activate(VARIATION_B);
      remember(VARIATION_B);
      return VARIATION_B;
    }

    activate(CONTROL);
    remember(CONTROL);
    return CONTROL;
  }

  var forced = forcedVariation();
  if (forced) {
    // No remember(), no SDK call: a forced preview must not poison the cache
    // or record an exposure that would skew the experiment's own numbers.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() a value the SDK never produced — it would hide the
      // failure on every later page view.
      if (!root.hasAttribute('data-nff-pdp')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
